import { useT } from "../../../shared/i18n/i18n";
import { Check, Link as LinkIcon, Loader2, Pause, Play, SkipBack, SkipForward, X } from "lucide-react";
import { useRef, useState } from "react";
import { cn } from "../../../shared/lib/cn";
import { Button } from "@/components/ui/button";
import type { ReplayMoment, ReplayStep } from "../model/use-sprint-replay";

const dayMs = 24 * 60 * 60 * 1000;

const toneClasses: Record<ReplayMoment["tone"], string> = {
  neutral: "text-muted-foreground",
  positive: "text-emerald-500",
  warning: "text-amber-500",
};

const toneDotClasses: Record<ReplayMoment["tone"], string> = {
  neutral: "bg-muted-foreground/45",
  positive: "bg-emerald-500",
  warning: "bg-amber-500",
};

function formatMoment(at: number) {
  const date = new Date(at);
  const day = new Intl.DateTimeFormat("en", { day: "numeric", month: "short", weekday: "short" }).format(date);
  const time = `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  return `${day} · ${time}`;
}

// The replay control surface shown above a view: play/pause with key-moment
// navigation, a real-time scrubber with per-day ticks and event markers, a
// range preset (whole sprint / last 24h) and a caption narrating the moment.
export function SprintReplayBar({
  canUseDayRange,
  caption,
  hasNextKey,
  hasPreviousKey,
  isLoading,
  onChangeRange,
  onCopyLink,
  onExit,
  onJumpToNextKey,
  onJumpToPreviousKey,
  onScrub,
  onTogglePlay,
  playing,
  range,
  steps,
  time,
  windowEnd,
  windowStart,
}: {
  canUseDayRange: boolean;
  caption: ReplayMoment[];
  hasNextKey: boolean;
  hasPreviousKey: boolean;
  isLoading: boolean;
  onChangeRange: (range: "day" | "sprint") => void;
  onCopyLink: () => void;
  onExit: () => void;
  onJumpToNextKey: () => void;
  onJumpToPreviousKey: () => void;
  onScrub: (at: number) => void;
  onTogglePlay: () => void;
  playing: boolean;
  range: "day" | "sprint";
  steps: ReplayStep[];
  time: number | null;
  windowEnd: number | null;
  windowStart: number | null;
}) {
  const t = useT();
  const ready = windowStart !== null && windowEnd !== null && time !== null;
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex flex-col gap-1 rounded-lg bg-muted/40 px-3 py-2">
      <div className="flex items-center gap-3">
        <div className="flex shrink-0 items-center gap-1">
          <Button
            aria-label={t("sprint.replayPrevious")}
            className="size-7 text-muted-foreground"
            disabled={!ready || !hasPreviousKey}
            onClick={onJumpToPreviousKey}
            size="icon-sm"
            title={t("sprint.replayPrevious")}
            variant="ghost"
          >
            <SkipBack className="size-3.5" />
          </Button>
          <Button
            aria-label={playing ? t("replay.pause") : t("replay.play")}
            className="size-7 rounded-full"
            disabled={!ready}
            onClick={onTogglePlay}
            size="icon-sm"
          >
            {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
          </Button>
          <Button
            aria-label={t("sprint.replayNext")}
            className="size-7 text-muted-foreground"
            disabled={!ready || !hasNextKey}
            onClick={onJumpToNextKey}
            size="icon-sm"
            title={t("sprint.replayNext")}
            variant="ghost"
          >
            <SkipForward className="size-3.5" />
          </Button>
        </div>

        {ready ? (
          <ReplayScrubber onScrub={onScrub} steps={steps} time={time} windowEnd={windowEnd} windowStart={windowStart} />
        ) : (
          <div className="flex h-8 flex-1 items-center gap-2 text-xs text-muted-foreground">
            {isLoading ? <Loader2 className="size-3.5 animate-spin" /> : null}
            {isLoading ? t("replay.loading") : t("replay.none")}
          </div>
        )}

        {canUseDayRange ? (
          <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-muted/50 p-0.5">
            {(
              [
                { key: "sprint", label: t("sprint.sprint") },
                { key: "day", label: "24h" },
              ] as const
            ).map((entry) => (
              <button
                className={cn(
                  "rounded px-1.5 py-0.5 text-[11px] transition-colors",
                  range === entry.key ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground",
                )}
                key={entry.key}
                onClick={() => onChangeRange(entry.key)}
                type="button"
              >
                {entry.label}
              </button>
            ))}
          </div>
        ) : null}

        <span className="w-32 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
          {time !== null ? formatMoment(time) : ""}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            aria-label={t("sprint.replayCopyLink")}
            className="size-7 text-muted-foreground"
            disabled={!ready}
            onClick={() => {
              onCopyLink();
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1_500);
            }}
            size="icon-sm"
            title={t("sprint.replayCopyLink")}
            variant="ghost"
          >
            {copied ? <Check className="size-3.5 text-emerald-500" /> : <LinkIcon className="size-3.5" />}
          </Button>
          <Button aria-label={t("sprint.exitReplay")} className="size-7 text-muted-foreground" onClick={onExit} size="icon-sm" variant="ghost">
            <X className="size-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex min-h-4 items-center gap-3 pl-[104px] pr-9">
        {caption.slice(-2).map((moment, index) => (
          <span className={cn("truncate text-xs", toneClasses[moment.tone])} key={`${moment.at}:${index}`}>
            {moment.message}
          </span>
        ))}
      </div>
    </div>
  );
}

function ReplayScrubber({
  onScrub,
  steps,
  time,
  windowEnd,
  windowStart,
}: {
  onScrub: (at: number) => void;
  steps: ReplayStep[];
  time: number;
  windowEnd: number;
  windowStart: number;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const range = Math.max(1, windowEnd - windowStart);
  const percentOf = (at: number) => `${((Math.min(Math.max(at, windowStart), windowEnd) - windowStart) / range) * 100}%`;

  function timeFromPointer(clientX: number) {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return windowStart;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return windowStart + ratio * range;
  }

  const dayTicks: number[] = [];
  for (let tick = startOfNextDay(windowStart); tick < windowEnd; tick += dayMs) dayTicks.push(tick);
  // One marker per step, colored by its most severe moment; key moments pop.
  const markers = steps.map((step) => ({
    at: step.at,
    message: step.moments.map((moment) => moment.message).join("\n"),
    tone: step.moments.some((moment) => moment.tone === "warning")
      ? ("warning" as const)
      : step.moments.some((moment) => moment.tone === "positive")
        ? ("positive" as const)
        : ("neutral" as const),
  }));

  return (
    <div
      className="relative h-8 min-w-0 flex-1 cursor-pointer touch-none select-none"
      onPointerDown={(event) => {
        // A press that lands on a marker dot snaps to its exact moment; the
        // drag that may follow still belongs to the track (capture below), so
        // the thumb can be grabbed even when it sits on top of a marker.
        const marker = (event.target as HTMLElement).closest?.("[data-marker-at]");
        const exactAt = marker ? Number(marker.getAttribute("data-marker-at")) : Number.NaN;
        onScrub(Number.isFinite(exactAt) ? exactAt : timeFromPointer(event.clientX));
        // Capture is best-effort: it throws for pointer ids the browser does
        // not consider active (e.g. synthetic events), and the scrub above
        // must land regardless.
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // drag-follow simply won't engage
        }
      }}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) onScrub(timeFromPointer(event.clientX));
      }}
      ref={trackRef}
    >
      <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-muted-foreground/20" />
      <div className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-primary/60" style={{ width: percentOf(time) }} />
      {dayTicks.map((tick) => (
        <span className="absolute top-1/2 h-2 w-px -translate-y-1/2 bg-muted-foreground/25" key={tick} style={{ left: percentOf(tick) }} />
      ))}
      {markers.map((marker) => (
        <button
          aria-label={marker.message}
          className={cn(
            "absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-transform hover:scale-150",
            marker.tone === "neutral" ? "size-1" : "size-1.5",
            toneDotClasses[marker.tone],
          )}
          data-marker-at={marker.at}
          key={marker.at}
          onClick={() => onScrub(marker.at)}
          style={{ left: percentOf(marker.at) }}
          title={marker.message}
          type="button"
        />
      ))}
      <span
        className="pointer-events-none absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary shadow-sm"
        style={{ left: percentOf(time) }}
      />
    </div>
  );
}

function startOfNextDay(at: number) {
  const date = new Date(at);
  date.setHours(24, 0, 0, 0);
  return date.getTime();
}
